import React, { useEffect, useMemo } from "react";
import { useActivityNoteStore } from "../../../../config/stores";
import { useSelector } from "react-redux";
import AntdModal from "../../../../components/common/AntdModal";
import { Form, message } from "antd";
import AppButton from "../../../../components/common/AppButton";
import AppInput from "../../../../components/common/AppInput";
import dayjs from "dayjs";
import { filter, find, isEmpty, map } from "lodash";
import PropTypes from "prop-types";
import InvitedParticipantsInput from "./InvitedParticipantsInput";
import {
  conceptNoteCellStyle,
  conceptNoteTableStyle,
  conceptNoteTextFields,
  INVITED_PARTICIPANTS_LABEL,
  subTitleColumnStyle,
  wrapOptionRender,
} from "./conceptNoteFields";

const requiredRule = [{ required: true, message: "This field is required" }];

const SubTitle = ({ label, hint, required = false }) => (
  <td style={conceptNoteCellStyle} className="fw-semibold">
    {label}
    {required && <span className="text-danger"> *</span>}
    {hint && <div className="small text-secondary fw-normal">{hint}</div>}
  </td>
);

SubTitle.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  required: PropTypes.bool,
};

const ActivityConceptNotesForm = () => {
  const activityConceptNoteStore = useActivityNoteStore();
  const {
    creating,
    activityNoteToUpdate,
    showCreatingActivityNote,
    creatingActivityNote,
    updatingActivityNote,
    metaData,
  } = activityConceptNoteStore;
  const { projectsDetails } = useSelector((state) => state.projectsState);
  const { approvedBudget } = useSelector((state) => state.budgetState);

  const [form] = Form.useForm();
  const strategicPlanId = Form.useWatch("strategic_plan_id", form);
  const thematicAreaId = Form.useWatch("thematic_area_id", form);

  const thematicAreas = useMemo(
    () =>
      find(metaData.strategicPlans, { id: strategicPlanId })
        ?.strategicPlanThematicAreas || [],
    [metaData, strategicPlanId]
  );
  const activities = useMemo(
    () =>
      find(thematicAreas, { id: thematicAreaId })
        ?.strategicPlansProposedActivities || [],
    [thematicAreas, thematicAreaId]
  );

  const closeModal = () => {
    activityConceptNoteStore.setCreating();
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((data) => {
        if (creating) {
          const budgetLine = find(approvedBudget, {
            id: data.budget_line_id,
          });
          data.title = budgetLine?.name;
          activityConceptNoteStore.createActivityNote(data);
        } else {
          activityConceptNoteStore.updateActivityNote(
            activityNoteToUpdate.id,
            data
          );
        }
      })
      .catch(() => {
        message.error("Please fill in all required fields (marked *)");
      });
  };

  useEffect(() => {
    if (!showCreatingActivityNote) return;
    form.resetFields();
    if (creating) {
      form.setFieldsValue({
        project_id: projectsDetails.id,
        donor: projectsDetails.donor,
      });
    } else {
      form.setFieldsValue({
        ...activityNoteToUpdate,
        date: activityNoteToUpdate?.date
          ? dayjs(activityNoteToUpdate.date)
          : undefined,
        donor: projectsDetails.donor,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreatingActivityNote, activityNoteToUpdate, creating]);

  useEffect(() => {
    if (isEmpty(metaData)) activityConceptNoteStore.getMetaData();
  }, [metaData]);

  return (
    <AntdModal
      title={
        creating ? "ADD ACTIVITY CONCEPT NOTE" : "EDIT ACTIVITY CONCEPT NOTE"
      }
      customSize={1100}
      open={showCreatingActivityNote}
      closeModal={closeModal}
      footer={[
        <AppButton
          text={
            creating
              ? "CREATE ACTIVITY CONCEPT NOTE"
              : "UPDATE ACTIVITY CONCEPT NOTE"
          }
          key={"create"}
          loading={creatingActivityNote || updatingActivityNote}
          onClick={onSubmit}
        />,
        <AppButton text="CANCEL" key={"cancel"} onClick={closeModal} danger />,
      ]}
    >
      <p className="text-center h5 mb-1">Amani Initiative Activity Concept Note</p>
      <p className="text-center small text-secondary mb-3">
        Fields marked <span className="text-danger">*</span> are required
      </p>
      <Form layout="vertical" form={form}>
        <table
          className="table table-sm table-bordered align-middle"
          style={conceptNoteTableStyle}
        >
          <thead className="table-light">
            <tr>
              <th style={subTitleColumnStyle}>Sub-Titles</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <SubTitle
                label="Project Name"
                hint="Picked from confirmed projects"
              />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="project_id"
                  type="select"
                  rules={[
                    { required: true, message: "Please select a project" },
                  ]}
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    disabled: true,
                  }}
                  options={[
                    { label: projectsDetails.name, value: projectsDetails.id },
                  ]}
                />
              </td>
            </tr>
            <tr>
              <SubTitle label="Funder" />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="donor"
                  type="select"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    disabled: true,
                  }}
                  options={[
                    {
                      label: projectsDetails.donor,
                      value: projectsDetails.donor,
                    },
                  ]}
                />
              </td>
            </tr>
            <tr>
              <SubTitle label="Budget Line" required />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="budget_line_id"
                  type="select"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "Select budget line",
                  }}
                  rules={requiredRule}
                  options={filter(approvedBudget, {
                    requires_concept_note: true,
                  }).map((budget) => ({
                    label: budget.name,
                    value: budget.id,
                  }))}
                />
              </td>
            </tr>
            <tr>
              <SubTitle label="Strategic Plan Priority Input" required />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="strategic_plan_id"
                  type="select"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "Select strategic plan",
                    onChange: () =>
                      form.setFieldsValue({
                        thematic_area_id: undefined,
                        activity_id: undefined,
                      }),
                  }}
                  rules={requiredRule}
                  options={map(metaData.strategicPlans, (strategicPlan) => ({
                    label: strategicPlan.title,
                    value: strategicPlan.id,
                  }))}
                />
              </td>
            </tr>
            <tr>
              <SubTitle label="Department/Thematic Area" required />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="thematic_area_id"
                  type="select"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    disabled: !strategicPlanId,
                    placeholder: strategicPlanId
                      ? "Select thematic area"
                      : "Select a strategic plan first",
                    onChange: () =>
                      form.setFieldsValue({ activity_id: undefined }),
                  }}
                  options={map(thematicAreas, (thematicArea) => ({
                    label: thematicArea.thematic_area,
                    value: thematicArea.id,
                  }))}
                  rules={requiredRule}
                />
              </td>
            </tr>
            <tr>
              <SubTitle
                label="Activity Name"
                hint="Picked from activities attached to the selected thematic area"
                required
              />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="activity_id"
                  type="select"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    disabled: !thematicAreaId,
                    placeholder: thematicAreaId
                      ? "Select activity"
                      : "Select a thematic area first",
                    optionRender: wrapOptionRender,
                  }}
                  rules={requiredRule}
                  options={map(activities, (activity) => ({
                    label: activity.activity,
                    value: activity.id,
                  }))}
                />
              </td>
            </tr>
            <tr>
              <SubTitle label="Activity Dates" required />
              <td style={conceptNoteCellStyle}>
                <AppInput
                  name="date"
                  type="date"
                  itemAttributes={{ className: "mb-0" }}
                  inputAttributes={{
                    variant: "borderless",
                    placeholder: "Select date",
                  }}
                  rules={requiredRule}
                />
              </td>
            </tr>
            {conceptNoteTextFields.map((field) => (
              <tr key={field.name}>
                <SubTitle label={field.label} required={field.required} />
                <td style={conceptNoteCellStyle}>
                  <AppInput
                    name={field.name}
                    type={field.bulleted ? "bullets" : "textarea"}
                    itemAttributes={{ className: "mb-0" }}
                    inputAttributes={{
                      variant: "borderless",
                      placeholder: `Enter ${field.label.toLowerCase()}`,
                      autoSize: { minRows: 2 },
                    }}
                    rules={field.required ? requiredRule : []}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2}>
                <p className="mb-2 fw-semibold">{INVITED_PARTICIPANTS_LABEL}</p>
                <InvitedParticipantsInput />
              </td>
            </tr>
          </tbody>
        </table>
      </Form>
    </AntdModal>
  );
};

export default ActivityConceptNotesForm;
